import { Loader2 } from "lucide-react";
import { FC, useState } from "react";
import { FormattedMessage } from "react-intl";

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";

interface RemoveBatesConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documentCount: number;
  onConfirm: () => Promise<void> | void;
}

/**
 * Confirmation gate shown before removing reference (Bates) numbers. Removal
 * restores the clean page images and clears the numbers from the document and
 * its exports. It can be re-applied afterwards, but the case counter does not
 * roll back, so re-applying later yields different numbers — we surface that
 * caveat before the user commits.
 */
export const RemoveBatesConfirmDialog: FC<RemoveBatesConfirmDialogProps> = ({
  open,
  onOpenChange,
  documentCount,
  onConfirm,
}) => {
  const [isConfirming, setIsConfirming] = useState(false);

  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch {
      // onConfirm surfaces its own error toast; keep the dialog open for retry.
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            <FormattedMessage
              id="bates.removeConfirm.title"
              defaultMessage="Remove Reference Numbers?"
            />
          </AlertDialogTitle>
          <AlertDialogDescription>
            <FormattedMessage
              id="bates.removeConfirm.description"
              defaultMessage="This will remove reference numbers from {count, plural, one {# document} other {# documents}} and from its exports. You can re-apply later, but the numbering won't be reused, so the numbers may differ."
              values={{ count: documentCount }}
            />
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            onClick={() => onOpenChange(false)}
            disabled={isConfirming}
          >
            <FormattedMessage id="cancel" defaultMessage="Cancel" />
          </AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={isConfirming}
            aria-busy={isConfirming}
          >
            {isConfirming && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <FormattedMessage
              id="bates.removeBates"
              defaultMessage="Remove Referencing"
            />
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
